import { useMemo, useState } from 'react';

import {
  Box,
  Button,
  HStack,
  IconButton,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text,
  Tooltip,
} from '@vimeo/bokeh';
import { Smile } from '@vimeo/bokeh-icons';

import { T } from '../../translations';
import { Reaction, ReactionCode } from '../../types';

export const REACTION_EMOJI_MAP: Record<ReactionCode, string> = {
  thumbsup: '👍',
  heart: '❤️',
  tada: '🎉',
  eyes: '👀',
  rocket: '🚀',
  completed: '✅',
  thumbsdown: '👎',
  heavy_plus_sign: '➕',
};

export const ALL_REACTION_CODES: ReactionCode[] = [
  'thumbsup',
  'heart',
  'tada',
  'eyes',
  'rocket',
  'completed',
  'thumbsdown',
  'heavy_plus_sign',
];

interface ReactionGroup {
  reactionCode: ReactionCode;
  count: number;
  isByCurrentUser: boolean;
  reactionUri: string | null; // uri of the current user's reaction, if any
  userDisplayNames: string[];
}

function groupReactions(reactions: Reaction[]): ReactionGroup[] {
  const map = new Map<ReactionCode, ReactionGroup>();
  for (const reaction of reactions) {
    const existing = map.get(reaction.reactionCode);
    if (existing) {
      existing.count += 1;
      existing.userDisplayNames.push(reaction.userDisplayName);
      if (reaction.isByCurrentUser) {
        existing.isByCurrentUser = true;
        existing.reactionUri = reaction.uri;
      }
    } else {
      map.set(reaction.reactionCode, {
        reactionCode: reaction.reactionCode,
        count: 1,
        isByCurrentUser: reaction.isByCurrentUser,
        reactionUri: reaction.isByCurrentUser ? reaction.uri : null,
        userDisplayNames: [reaction.userDisplayName],
      });
    }
  }
  // Return in the order they appear in ALL_REACTION_CODES
  return ALL_REACTION_CODES.map((code) => map.get(code)).filter(Boolean) as ReactionGroup[];
}

interface ReactionBadgeProps {
  group: ReactionGroup;
  onAdd: (code: ReactionCode) => void;
  onRemove: (uri: string) => void;
}

const ReactionBadge = ({ group, onAdd, onRemove }: ReactionBadgeProps) => {
  const emoji = REACTION_EMOJI_MAP[group.reactionCode];

  const tooltipLabel = useMemo(() => {
    const namesLength = group.userDisplayNames.length;
    if (!namesLength) return '';
    if (namesLength === 1) return group.userDisplayNames[0];
    if (namesLength > 5) return T.OverLimitReactions(group.userDisplayNames.slice(0, 5));
    return T.UnderLimitReactions(
      group.userDisplayNames.slice(0, -1),
      group.userDisplayNames[namesLength - 1],
    );
  }, [group.userDisplayNames]);

  const handleClick = () => {
    if (group.isByCurrentUser && group.reactionUri) {
      if (group.reactionUri.includes('optimistic')) {
        // If the reaction is optimistic, we can wait for revalidate to finish adding the reaction.
        return;
      }
      onRemove(group.reactionUri);
    } else {
      onAdd(group.reactionCode);
    }
  };

  return (
    <Tooltip label={tooltipLabel} placement="top" isDisabled={!tooltipLabel}>
      <Button
        onClick={handleClick}
        variant="tertiary"
        borderRadius="lg"
        px="10px"
        py="2px"
        width="42px"
        height="24px"
        border="1px solid"
        borderColor="button-secondary-hover"
        backgroundColor={group.isByCurrentUser ? 'button-secondary-default' : 'transparent'}
        _dark={{
          backgroundColor: group.isByCurrentUser ? 'button-secondary-default' : 'transparent',
          _hover: {
            backgroundColor: 'button-secondary-hover',
          },
        }}
      >
        <HStack spacing="4px">
          <Box as="span" lineHeight={1} fontSize="14px">
            {emoji}
          </Box>
          <Text variant="heading-xs">{group.count}</Text>
        </HStack>
      </Button>
    </Tooltip>
  );
};

interface ReactionPickerProps {
  reactions: Reaction[];
  onAdd: (code: ReactionCode) => void;
  onRemove: (uri: string) => void;
}

export const ReactionPicker = ({ reactions, onAdd, onRemove }: ReactionPickerProps) => {
  const groups = groupReactions(reactions);
  const currentUserReactionCodes = new Set(
    reactions.filter((r) => r.isByCurrentUser).map((r) => r.reactionCode),
  );
  const allReactionsApplied = currentUserReactionCodes.size >= ALL_REACTION_CODES.length;
  const [showEmojiPopover, setShowEmojiPopover] = useState(false);

  const toggleEmojiPopover = () => setShowEmojiPopover((prev) => !prev);

  return (
    <HStack spacing="4px" flexWrap="wrap">
      {groups.map((group) => (
        <ReactionBadge key={group.reactionCode} group={group} onAdd={onAdd} onRemove={onRemove} />
      ))}

      {!allReactionsApplied && (
        <Popover
          placement="top"
          flip={false}
          isOpen={showEmojiPopover}
          onClose={() => setShowEmojiPopover(false)}
        >
          <PopoverTrigger>
            <IconButton
              aria-label={T.AddReaction}
              icon={<Smile />}
              variant="tertiary"
              size="xs"
              height="26px"
              width="36px"
              borderRadius="lg"
              border="1px dashed"
              borderColor="button-secondary-hover"
              onClick={() => toggleEmojiPopover()}
            />
          </PopoverTrigger>
          <Portal>
            <PopoverContent>
              <PopoverBody>
                <HStack>
                  {ALL_REACTION_CODES.map((code) => {
                    const alreadyApplied = currentUserReactionCodes.has(code);
                    if (alreadyApplied) {
                      return null;
                    }
                    return (
                      <Button
                        variant="tertiary"
                        key={code}
                        onClick={() => {
                          onAdd(code);
                          toggleEmojiPopover();
                        }}
                        borderRadius="xs"
                        height="26px"
                        width="26px"
                        p="6px"
                        gap="2px"
                        backgroundColor="transparent"
                        _hover={{
                          backgroundColor: 'fill-container-hover',
                          transform: 'scale(1.2)',
                        }}
                        transition="transform 0.1s ease"
                      >
                        {REACTION_EMOJI_MAP[code]}
                      </Button>
                    );
                  })}
                </HStack>
              </PopoverBody>
            </PopoverContent>
          </Portal>
        </Popover>
      )}
    </HStack>
  );
};
